import type { useLocalizedNamesStore } from '../../../stores/localizedNames';

export interface RosterStructure {
  domain?: { name?: string; tech_name?: string; id?: number };
  faction?: { name?: string; tech_name?: string; id?: number };
  hero?: { name?: string; tech_name?: string; id?: number; point_cost: number };
  units?: Array<{ name?: string; tech_name?: string; id?: number; quantity: number; point_cost: number }>;
  spells?: Array<{ name?: string; tech_name?: string; id?: number; quantity: number }>;
  equipment?: Array<{ name?: string; tech_name?: string; id?: number; quantity: number; point_cost: number }>;
}

export interface TournamentRoster {
  id: number;
  faction_name: string;
  points_overall?: number;
  points_left?: number;
  structure?: RosterStructure;
}

export const formatRosterToText = async (
  roster: TournamentRoster,
  namesStore: ReturnType<typeof useLocalizedNamesStore>
): Promise<string> => {
  if (!roster.structure) {
    return `Фракция: ${roster.faction_name}\nСтруктура недоступна`;
  }

  // Load names for all entities
  const items: Array<{ type: 'domain' | 'faction' | 'hero' | 'unit' | 'spell' | 'equipment'; tech_name?: string; id?: number }> = [];

  if (roster.structure.domain) {
    items.push({
      type: 'domain',
      tech_name: roster.structure.domain.tech_name,
      id: roster.structure.domain.id,
    });
  }

  if (roster.structure.faction) {
    items.push({
      type: 'faction',
      tech_name: roster.structure.faction.tech_name,
      id: roster.structure.faction.id,
    });
  }

  if (roster.structure.hero) {
    items.push({
      type: 'hero',
      tech_name: roster.structure.hero.tech_name,
      id: roster.structure.hero.id,
    });
  }

  roster.structure.units?.forEach(unit => {
    items.push({
      type: 'unit',
      tech_name: unit.tech_name,
      id: unit.id,
    });
  });

  roster.structure.spells?.forEach(spell => {
    items.push({
      type: 'spell',
      tech_name: spell.tech_name,
      id: spell.id,
    });
  });

  roster.structure.equipment?.forEach(eq => {
    items.push({
      type: 'equipment',
      tech_name: eq.tech_name,
      id: eq.id,
    });
  });

  await namesStore.ensureNamesLoaded(items);

  const sections: string[] = [];

  // Domain
  if (roster.structure.domain) {
    const domainName = namesStore.getName(
      'domain',
      roster.structure.domain.tech_name,
      roster.structure.domain.id
    ) || roster.structure.domain.name;
    if (domainName) {
      sections.push(`🏛️ Домен: ${domainName}`);
    }
  }

  // Faction
  if (roster.structure.faction) {
    const factionName = namesStore.getName(
      'faction',
      roster.structure.faction.tech_name,
      roster.structure.faction.id
    ) || roster.structure.faction.name;
    if (factionName) {
      sections.push(`⚔️ Фракция: ${factionName}`);
    }
  }

  // Hero
  if (roster.structure.hero) {
    const hero = roster.structure.hero;
    const heroName = namesStore.getName(
      'hero',
      hero.tech_name,
      hero.id
    ) || hero.name;
    if (heroName) {
      sections.push(`👑 Герой: ${heroName} (${hero.point_cost} очков)`);
    }
  }

  // Units
  if (roster.structure.units && roster.structure.units.length > 0) {
    const unitsText = roster.structure.units
      .map(unit => {
        const unitName = namesStore.getName(
          'unit',
          unit.tech_name,
          unit.id
        ) || unit.name || 'Unknown';
        const totalPoints = unit.quantity * unit.point_cost;
        return `  • ${unitName} (${unit.quantity} x ${unit.point_cost} = ${totalPoints} очков)`;
      })
      .join('\n');
    sections.push(`👥 Отряды:\n${unitsText}`);
  }

  // Spells
  if (roster.structure.spells && roster.structure.spells.length > 0) {
    const spellsText = roster.structure.spells
      .map(spell => {
        const spellName = namesStore.getName(
          'spell',
          spell.tech_name,
          spell.id
        ) || spell.name || 'Unknown';
        return `  • ${spellName} (${spell.quantity})`;
      })
      .join('\n');
    sections.push(`🔮 Заклинания:\n${spellsText}`);
  }

  // Equipment
  if (roster.structure.equipment && roster.structure.equipment.length > 0) {
    const equipmentText = roster.structure.equipment
      .map(item => {
        const itemName = namesStore.getName(
          'equipment',
          item.tech_name,
          item.id
        ) || item.name || 'Unknown';
        const totalPoints = item.quantity * item.point_cost;
        return `  • ${itemName} (${item.quantity} x ${item.point_cost} = ${totalPoints} очков)`;
      })
      .join('\n');
    sections.push(`⚔️ Снаряжение:\n${equipmentText}`);
  }

  return sections.join('\n\n');
};
